/**
 * Service selection utilities — shared across all booking surfaces.
 *
 * Consolidates duplicated logic from:
 * - apps/web/src/app/appointment/[id]/components/index.tsx (referenceId filtering)
 * - apps/web/src/app/templates/light/BookingSectionClient.tsx (referenceId filtering)
 *
 * Note: The Location model has been removed. Schedule now carries address
 * fields directly.
 */

import type {
  ServiceItem,
  SelectedServiceItem,
  ServiceOptionItem,
  OpeningsServicePayload,
  MemberOpeningsItem,
  MemberServiceInfo,
} from "./types";

/* ─── Member identity ────────────────────────────────────────────────── */

/**
 * Resolve a member's display-safe user ID.
 *
 * Priority: `userId` → `teamMemberId` → `username` → fallback.
 * Used across all booking surfaces for consistent member identification.
 */
export function resolveMemberId(
  member: Pick<MemberOpeningsItem, "userId" | "teamMemberId" | "username">,
  fallback = "",
): string {
  return member.userId ?? member.teamMemberId ?? member.username ?? fallback;
}

/* ─── Member service pricing ─────────────────────────────────────────── */

/**
 * Group a member's services by `referenceId`, summing price + duration for
 * entries that share the same reference (i.e. multi-option combos).
 *
 * Returns a sorted array (cheapest first) for display.
 */
export function groupMemberServices(
  services: MemberServiceInfo[],
): Array<MemberServiceInfo & { price: number; duration: number }> {
  const grouped = services.reduce<
    Array<MemberServiceInfo & { price: number; duration: number }>
  >((acc, service) => {
    const existing = acc.find(
      (item) => item.referenceId === service.referenceId,
    );
    if (existing) {
      existing.price += service.price ?? 0;
      existing.duration += service.duration ?? 0;
    } else {
      acc.push({
        ...service,
        price: service.price ?? 0,
        duration: service.duration ?? 0,
      });
    }
    return acc;
  }, []);
  return grouped.sort((a, b) => a.price - b.price);
}

/**
 * Calculate the combined price for a set of selected services, using
 * the member-specific variant prices when available.
 */
export function getMemberCombinedPrice(
  selectedServices: SelectedServiceItem[],
  memberServices: MemberServiceInfo[],
): number {
  return selectedServices.reduce((sum, sel) => {
    const ms = memberServices.find(
      (s) => s.referenceId === sel.id || s.id === sel.id,
    );
    return (
      sum +
      (ms?.option?.price ?? ms?.price ?? sel.option?.price ?? sel.price ?? 0)
    );
  }, 0);
}

/**
 * Calculate the combined duration for a set of selected services, using
 * the member-specific variant durations when available.
 */
export function getMemberCombinedDuration(
  selectedServices: SelectedServiceItem[],
  memberServices: MemberServiceInfo[],
): number {
  return selectedServices.reduce((sum, sel) => {
    const ms = memberServices.find(
      (s) => s.referenceId === sel.id || s.id === sel.id,
    );
    return (
      sum +
      (ms?.option?.duration ??
        ms?.duration ??
        sel.option?.duration ??
        sel.duration ??
        0)
    );
  }, 0);
}

/* ─── Service filtering ─────────────────────────────────────────────── */

/**
 * Filter to business-level services only (those without a `referenceId`).
 * Team-member clones have a non-null `referenceId` pointing at the parent service.
 *
 * Falls back to the full list if filtering would leave zero results.
 */
export function filterBusinessServices<T extends ServiceItem>(
  services: T[],
): T[] {
  const businessOnly = services.filter(
    (s) => s.referenceId === null || s.referenceId === undefined,
  );
  return businessOnly.length > 0 ? businessOnly : services;
}

/* ─── Duration & price calculation ───────────────────────────────────── */

/**
 * Get the effective duration for a single selected service, accounting for
 * its selected option (which overrides the base duration).
 */
export function getServiceDuration(service: SelectedServiceItem): number {
  if (service.option) {
    return service.option.duration;
  }
  return service.duration ?? 0;
}

/**
 * Get the effective price for a single selected service, accounting for
 * its selected option (which overrides the base price).
 */
export function getServicePrice(service: SelectedServiceItem): number {
  if (service.option) {
    return service.option.price;
  }
  return service.price ?? 0;
}

/**
 * Calculate total duration across all selected services + their options.
 */
export function calculateCombinedDuration(
  services: SelectedServiceItem[],
): number {
  return services.reduce((sum, s) => sum + getServiceDuration(s), 0);
}

/**
 * Calculate total price across all selected services + their options.
 */
export function calculateCombinedPrice(
  services: SelectedServiceItem[],
): number {
  return services.reduce((sum, s) => sum + getServicePrice(s), 0);
}

/* ─── Option resolution ──────────────────────────────────────────────── */

/**
 * Resolve a service option by ID. Returns undefined if optionId is not
 * provided or not found (meaning "Regular" / base service applies).
 */
export function resolveServiceOption(
  service: ServiceItem,
  optionId?: string,
): ServiceOptionItem | undefined {
  if (!optionId || !service.options?.length) return undefined;
  return service.options.find((o) => o.id === optionId);
}

/* ─── API payload builders ───────────────────────────────────────────── */

/**
 * Build the `services` array for the location openings API call.
 *
 * Each entry contains the service ID and optionally an option ID.
 * This ensures multi-service + option combos are sent correctly.
 */
export function buildOpeningsServicePayload(
  services: Array<{ id: string; option?: { id: string } | null }>,
): OpeningsServicePayload[] {
  return services.map((s) => ({
    serviceId: s.id,
    ...(s.option?.id ? { optionId: s.option.id } : {}),
  }));
}

/* ─── Member-service lookup ──────────────────────────────────────────── */

/**
 * Find the member-specific service variant that matches a business-level
 * service ID.  Member services have `referenceId` pointing at the parent.
 *
 * Used when building a BookingSelection from a member's openings response.
 */
export function findMemberService(
  member: MemberOpeningsItem,
  serviceId: string,
): MemberServiceInfo | undefined {
  return member.services.find(
    (s) => s.referenceId === serviceId || s.id === serviceId,
  );
}

/**
 * Return a copy of `selectedServices` with provider-specific values overlaid
 * when a matching member service exists. A missing/null member description is
 * authoritative "no description" and clears the business-level copy.
 *
 * Payment policy is overlaid only when the member service states it. The API
 * resolves a clone's requirement from its business parent, so a stated value
 * is the exact effective policy; an absent one means "this payload did not
 * say" and must never overwrite the business-level requirement — downgrading
 * it would quietly turn a paid service into a free one in the UI.
 * `paymentRequirement` and `depositAmount` travel together: a requirement
 * must never pair with another record's amount.
 */
export function applyMemberPricing<T extends SelectedServiceItem>(
  selectedServices: T[],
  memberServices: MemberServiceInfo[],
): T[] {
  return selectedServices.map((sel) => {
    const ms = memberServices.find(
      (s) => s.referenceId === sel.id || s.id === sel.id,
    );
    if (!ms) return sel;
    return {
      ...sel,
      ...(typeof ms.price === "number" ? { price: ms.price } : {}),
      ...(typeof ms.duration === "number" ? { duration: ms.duration } : {}),
      description: ms.description ?? null,
      ...(typeof ms.up === "boolean" ? { up: ms.up } : {}),
      ...(typeof ms.hasPayAdvance === "boolean"
        ? { hasPayAdvance: ms.hasPayAdvance }
        : {}),
      ...(ms.paymentRequirement
        ? {
            paymentRequirement: ms.paymentRequirement,
            depositAmount:
              typeof ms.depositAmount === "number" ? ms.depositAmount : null,
          }
        : {}),
      ...(ms.option ? { option: { ...ms.option } } : {}),
    };
  });
}

/* ─── Payment requirement disclosure ────────────────────────────────── */

/**
 * The effective upfront-payment requirement for a selection.
 *
 * `UNKNOWN` is a distinct outcome, not a synonym for `NONE`: a payload that
 * does not state a selected service's requirement has to disclose
 * conditionally ("Payment may be required") rather than promise that
 * nothing is due. `FULL` outranks `DEPOSIT`, which outranks `NONE`, because a
 * mixed cart's strictest item decides what the customer is asked to pay.
 */
export type SelectionPaymentRequirement =
  | "NONE"
  | "DEPOSIT"
  | "FULL"
  | "UNKNOWN";

/** The policy fields a disclosure reads off one selected service. */
export type ServicePaymentPolicy = Pick<
  SelectedServiceItem,
  "paymentRequirement" | "hasPayAdvance" | "price" | "depositAmount" | "option"
>;

/**
 * Normalize one service's stated policy the way the API does
 * (`normalizeServicePaymentRequirement` in
 * `apps/api/src/modules/appointments/helpers.ts`): the legacy
 * `hasPayAdvance` flag means DEPOSIT whenever the enum is not stricter.
 * `{ paymentRequirement: "NONE", hasPayAdvance: true }` is a supported
 * payload — the openings mapper sends the raw enum and the flag separately —
 * and the server charges it, so the UI must not read it as free.
 *
 * `UNKNOWN` stays distinct from `NONE`: an omitted enum with no legacy flag
 * means "this payload did not say", not "nothing is due".
 */
export function resolveServicePaymentRequirement(
  service: Pick<ServicePaymentPolicy, "paymentRequirement" | "hasPayAdvance">,
): SelectionPaymentRequirement {
  if (service.paymentRequirement === "FULL") return "FULL";
  if (service.paymentRequirement === "DEPOSIT") return "DEPOSIT";
  if (service.hasPayAdvance) return "DEPOSIT";
  return service.paymentRequirement === "NONE" ? "NONE" : "UNKNOWN";
}

/** The effective price of a selection row: its option's when one is chosen. */
function effectiveServicePrice(service: ServicePaymentPolicy): number | null {
  const price = service.option ? service.option.price : service.price;
  return typeof price === "number" && Number.isFinite(price) ? price : null;
}

/**
 * Whether a service leaves anything due at the appointment, mirroring
 * `resolveServicePayment` in `packages/database/src/payments.ts`: a deposit
 * that is unset, zero, or at least the effective price falls back to the
 * whole price, so the customer pays everything up front and nothing is due
 * later. A `NONE` row is itself a remainder — it is paid at the appointment.
 * An effective price the payload does not state keeps the remainder unknown,
 * which counts as due later so the copy never over-promises a completed
 * payment.
 *
 * A *stated* zero price is not unknown: the server caps the deposit at the
 * price, so a free row charges nothing and leaves nothing due later. Reading
 * it as a remainder made a whole-total payment beside a free service say
 * "Deposit required" (review r2 of #647).
 */
function leavesAmountDueLater(
  service: ServicePaymentPolicy,
  requirement: SelectionPaymentRequirement,
): boolean {
  const price = effectiveServicePrice(service);
  if (price === null) return true;
  if (requirement === "NONE") return price > 0;
  if (price <= 0) return false;
  const deposit = service.depositAmount;
  if (typeof deposit !== "number" || !Number.isFinite(deposit) || deposit <= 0) {
    return false;
  }
  return deposit < price;
}

/**
 * Whether a service contributes anything to the charge taken when the booking
 * is made, mirroring `resolveServicePayment` in
 * `packages/database/src/payments.ts`: `chargeNow` is the effective price for
 * `FULL` and the deposit capped at that price for `DEPOSIT`, so a row whose
 * effective price is a stated zero charges nothing however strict its policy
 * reads. The server gates the payment method on the charge
 * (`paymentSummary.requiresPaymentMethod`, `chargeNow > 0`), not on the enum,
 * so the policy alone never establishes that something is due now.
 *
 * An effective price the payload does not state leaves the charge unknown, so
 * the stricter disclosure stands rather than promising a free booking
 * (review r3 of #647).
 */
function chargesUpfront(
  service: ServicePaymentPolicy,
  requirement: SelectionPaymentRequirement,
): boolean {
  if (requirement !== "FULL" && requirement !== "DEPOSIT") return false;
  const price = effectiveServicePrice(service);
  if (price === null) return true;
  return price > 0;
}

/**
 * Resolve the requirement a selection discloses, from services that already
 * carry effective policy **and effective prices** (run member selections
 * through {@link applyMemberPricing} first). It weighs the charge, so a
 * business catalog row — whose price is not any member's price — must use
 * {@link catalogSelectionMayRequirePayment} instead.
 */
export function resolveSelectionPaymentRequirement(
  services: ServicePaymentPolicy[],
): SelectionPaymentRequirement {
  if (services.length === 0) return "NONE";

  let unknown = false;
  let full = false;
  let deposit = false;
  let dueLater = false;
  for (const service of services) {
    const requirement = resolveServicePaymentRequirement(service);
    if (requirement === "UNKNOWN") {
      unknown = true;
    } else if (chargesUpfront(service, requirement)) {
      // A free prepaid row is charged nothing, so it decides nothing about
      // what the customer is asked to pay now.
      if (requirement === "FULL") full = true;
      else deposit = true;
    }
    if (requirement !== "UNKNOWN" && leavesAmountDueLater(service, requirement)) {
      dueLater = true;
    }
  }
  if (full) return "FULL";
  // A deposit that consumes the whole selected total is a payment, not a
  // deposit: `booking-flow.instructions.md` § 6 forbids "Deposit required"
  // when the legacy deposit falls back to the whole total. An unstated
  // service in the cart keeps the remainder unknown, so it stays the deposit
  // disclosure.
  if (deposit) return dueLater || unknown ? "DEPOSIT" : "FULL";
  return unknown ? "UNKNOWN" : "NONE";
}

/**
 * Whether a *business catalog* selection can still require a payment once a
 * member's own terms arrive — the only question the pre-member disclosure can
 * answer.
 *
 * It reads the stated policy alone and never the price, because the catalog
 * price is not any member's price: clones own their prices independently
 * (`schedule-service-hierarchy.instructions.md` § 7) while inheriting the
 * business payment policy, so a $0 catalog row can be a $50 clone the server
 * charges. Treating the catalog's zero price as the member's hid that charge
 * behind the slot click (review r4 of #647) — the same reason the location
 * selector shows no business prices.
 *
 * Once effective per-member prices are in hand, the charge decides the exact
 * copy: use {@link resolveSelectionPaymentRequirement} there.
 */
export function catalogSelectionMayRequirePayment(
  services: Array<
    Pick<ServicePaymentPolicy, "paymentRequirement" | "hasPayAdvance">
  >,
): boolean {
  return services.some(
    (service) => resolveServicePaymentRequirement(service) !== "NONE",
  );
}

/**
 * The copy catalog key that discloses a selection's payment requirement, or
 * null when nothing is due and no disclosure is needed
 * (`booking-flow.instructions.md` § 11, Requirements/request/payment).
 */
export type PaymentRequirementCopyKey =
  | "booking.requirement.payment"
  | "booking.requirement.deposit"
  | "booking.requirement.paymentMayApply";

export function paymentRequirementCopyKey(
  requirement: SelectionPaymentRequirement,
): PaymentRequirementCopyKey | null {
  switch (requirement) {
    case "FULL":
      return "booking.requirement.payment";
    case "DEPOSIT":
      return "booking.requirement.deposit";
    case "UNKNOWN":
      return "booking.requirement.paymentMayApply";
    default:
      return null;
  }
}

/**
 * English source strings for the requirement keys. Typed copy injection does
 * not exist yet (`booking-flow.instructions.md` § 11), so every surface reads
 * the same strings from here instead of hardcoding its own wording.
 */
export const REQUIREMENT_COPY: Readonly<
  Record<PaymentRequirementCopyKey | "booking.requirement.request", string>
> = {
  "booking.requirement.request": "Request required",
  "booking.requirement.deposit": "Deposit required",
  "booking.requirement.payment": "Payment required",
  "booking.requirement.paymentMayApply": "Payment may be required",
};

/**
 * English source strings for the request branch a request-required selection
 * shows instead of openings (`booking-flow.instructions.md` § 11).
 */
export const REQUEST_COPY: Readonly<
  Record<"booking.request.explanation", string>
> = {
  "booking.request.explanation":
    "The selected service must be reviewed before an appointment can be booked.",
};

/**
 * The label a member row or selection summary shows for its requirement, or
 * null when nothing has to be disclosed. A request-required selection is
 * disclosed as the request, which precedes any payment
 * (`booking-flow.instructions.md` § 6).
 */
export function resolveRequirementLabel({
  requestRequired,
  paymentRequirement,
}: {
  requestRequired?: boolean;
  paymentRequirement: SelectionPaymentRequirement;
}): string | null {
  if (requestRequired) return REQUIREMENT_COPY["booking.requirement.request"];
  const key = paymentRequirementCopyKey(paymentRequirement);
  return key ? REQUIREMENT_COPY[key] : null;
}

/**
 * The requirement a service picker discloses for `services` before a time or
 * contact details are chosen (`booking-flow.instructions.md` § 6, § 12
 * "Service catalog/card"; `BF-DRIFT-005`), or null when there is nothing to
 * disclose. A request-required service is disclosed as the request, which
 * precedes any payment.
 *
 * `exactTerms` says whose terms the rows carry:
 * - `true`: one provider's own effective terms — the member page's services
 *   or a quote — so the charge decides the exact label
 *   ({@link resolveSelectionPaymentRequirement}).
 * - `false`: business catalog rows, whose price is no member's price, so a
 *   payment can only be disclosed conditionally
 *   ({@link catalogSelectionMayRequirePayment}). Each member row states its
 *   exact terms once the openings arrive.
 */
export function resolveCatalogRequirementLabel(
  services: Array<
    ServicePaymentPolicy & Pick<SelectedServiceItem, "hasConsultation">
  >,
  { exactTerms }: { exactTerms: boolean },
): string | null {
  if (services.length === 0) return null;
  if (services.some((service) => service.hasConsultation)) {
    return REQUIREMENT_COPY["booking.requirement.request"];
  }
  if (exactTerms) {
    return resolveRequirementLabel({
      paymentRequirement: resolveSelectionPaymentRequirement(services),
    });
  }
  return catalogSelectionMayRequirePayment(services)
    ? REQUIREMENT_COPY["booking.requirement.paymentMayApply"]
    : null;
}

/**
 * Check whether any of the selected services require a consultation
 * for a given member.
 */
export function hasConsultationRequired(
  member: MemberOpeningsItem,
  selectedServices: SelectedServiceItem[],
): boolean {
  return selectedServices.some((selected) => {
    const memberSvc = findMemberService(member, selected.id);
    return memberSvc?.hasConsultation === true;
  });
}
